<template>
  <section v-if="ratesInTotal && ratesInTotal > 0" id="comments" class="comments">
    <b-loading :is-full-page="false" :active.sync="loading" :can-cancel="false" />
    <div class="columns is-desktop is-align-items-end">
      <div v-if="quantity" class="column is-full-tablet is-half-desktop">
        <h2 class="title is-4">
          {{ $t('comments.ratings') }}
        </h2>
        <div class="average-rating">
          <b-rate size="is-medium" :value="averageRate" disabled class="stars" />
          {{ $t('comments.averageRating', { count: averageRate }) }}
        </div>
        <div class="based-on has-text-grey-dark">
          {{ $t('comments.basedOn', { count: ratesInTotal }) }}
        </div>

        <div class="rating-bars">
          <div v-for="row in Object.keys(quantity).reverse()" :key="row" class="rating-bar" @click="changeRating(row)">
            <div :class="row === rating ? 'has-text-weight-bold' : ''">
              {{ Number(row) }}
            </div>
            <b-progress
              :value="quantity[row].averageQuantity"
              :type="row === rating ? 'is-warning' : 'is-rating'"
              size="is-medium"
              class="bar"
            />
            <div :class="row === rating ? 'percentage has-text-weight-bold' : 'percentage'">
              {{ quantity[row].averageQuantity }}%
            </div>
          </div>
        </div>

        <div v-if="rating !== null" class="all-reviews">
          <b-button expanded class="all-reviews-button" @click.prevent="changeRating(null)">
            {{ $t('comments.showAllReviews') }}
          </b-button>
        </div>
      </div>
      <div v-if="images && images.length" class="column">
        <span class="has-text-weight-bold">{{ $t('comments.userImages') }}</span>

        <customer-gallery :images="images" />
      </div>
    </div>
    <div>
      <h3 class="title is-4">
        <span>
          {{ $t(`comments.reviews.${rating}`) }}
        </span>
        <span>({{ rating ? quantity[rating].quantity : ratesInTotal }})</span>
      </h3>
      <div class="reviews">
        <div v-for="(review, index) in data" :key="index" class="review">
          <div class="review-header">
            <avatar :name="review.user.displayName" :avatar="review.user.avatar" target="review" />
            <span class="review-date has-text-grey-dark">{{ $moment(review.createdAt).format('LL') }}</span>
          </div>
          <div class="review-rating">
            <b-rate size="is-small" :value="review.star" disabled class="stars" />
            <div class="verified">
              <b-icon icon="checkmark" />
              {{ $t('comments.verifiedPurchase') }}
            </div>
          </div>
          <div class="review-product">
            {{ review.productTitle }}
          </div>
          <div v-if="review.comment" class="review-comment">
            {{ review.comment.text }}
            <!-- <span class="is-pulled-right">
              <b-button
                icon-left="like"
                class="is-small"
                :loading="loadingVote"
                :class="{'is-static': !user, 'is-success is-light': review.comment.getVoteReactions.user === 'UP_VOTE'}"
                :disabled="review.comment.getVoteReactions.user === 'DOWN_VOTE'"
                @click="voteReaction('UP_VOTE', review.comment.id, review.comment.getVoteReactions)"
              >
                {{ review.comment.getVoteReactions.UP_VOTE }}
              </b-button>
              <b-button
                icon-left="dislike"
                class="is-small"
                :loading="loadingVote"
                :class="{'is-static': !user, 'is-danger is-light': review.comment.getVoteReactions.user === 'DOWN_VOTE'}"
                :disabled="review.comment.getVoteReactions.user === 'UP_VOTE'"
                @click="voteReaction('DOWN_VOTE', review.comment.id, review.comment.getVoteReactions)"
              >
                {{ review.comment.getVoteReactions.DOWN_VOTE }}
              </b-button>
            </span> -->
          </div>
          <gallery v-if="review.comment && review.comment.filePivots.length > 0" :images="review.comment.filePivots" without-main-photo :set-thumbnails-page="10" :responsive="{0: {items: 4}, 600: {items: 6}, 700: {items: 10}}" />
        </div>
        <div v-if="total === 0" class="review">
          <div class="no-reviews">
            <span class="has-text-grey-light">{{ $t('comments.noReviews') }}</span>
          </div>
        </div>
        <b-pagination
          v-if="total > 0"
          :total="total"
          :current="page"
          :per-page="perPage"
          :range-before="2"
          :range-after="2"
          class="m-t-20"
          :aria-next-label="$t('pagination.aria_next_label')"
          :aria-previous-label="$t('pagination.aria_previous_label')"
          :aria-page-label="$t('pagination.aria_page_label')"
          :aria-current-label="$t('pagination.aria_current_label')"
          @change="onPageChangePagination"
        />
      </div>
    </div>
  </section>
</template>

<script>
import gallery from 'marketplace-front-core/components/gallery/ZoomGallery'
import Avatar from 'marketplace-front-core/components/shared/Avatar'
import CustomerGallery from 'marketplace-front-core/components/comments/CustomerGallery'

export default {
  name: 'Comments',
  components: {
    gallery,
    Avatar,
    CustomerGallery
  },
  props: {
    offerId: {
      type: Number,
      default: null
    },
    initialComments: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      data: this.initialComments ? this.initialComments.elements : [],
      quantity: this.initialComments ? this.initialComments.quantity : null,
      ratesInTotal: this.initialComments ? this.initialComments.ratesInTotal : null,
      averageRate: this.initialComments ? this.initialComments.averageRate : null,
      rating: null,
      loading: false,
      loadingVote: false,
      total: this.initialComments ? this.initialComments.all : null,
      page: 1,
      perPage: 10,
      rateId: null,
      images: []
    }
  },
  watch: {
    offerId (val, oldVal) {
      if (oldVal !== null || !this.initialComments) {
        this.onPageChange(1)
      }
      this.loadPhotos()
    }
  },
  mounted () {
    const query = this._clone(this.$route.query)
    if (query && query.rateId) {
      this.rateId = query.rateId
      this.$nextTick(() => {
        delete query.rateId
        this.$router.replace({ query })
      })
    }
    if (!this.initialComments) {
      this.loadAsyncData()
    }
    this.loadPhotos()
  },
  methods: {
    loadAsyncData () {
      if (this.offerId) {
        this.loading = true
        this.$api.comment.offer(this.offerId, { page: this.page, limit: this.perPage, rating: this.rating, id: this.rateId })
          .then((res) => {
            this.loading = false
            this.data = res.elements
            this.total = res.all
            this.quantity = res.quantity
            this.ratesInTotal = res.ratesInTotal
            this.averageRate = res.averageRate
            this.rateId = null
          })
          .catch(() => {
            this.loading = false
            this.data = []
            this.total = 0
            this.quantity = null
            this.ratesInTotal = null
            this.averageRate = null
          })
      } else {
        this.data = null
      }
    },
    onPageChangePagination (page) {
      this.onPageChange(page)
      this.$nextTick(() => {
        this.scrollToCommentsTop()
      })
    },
    onPageChange (page) {
      this.page = page
      this.loadAsyncData()
    },
    changeRating (rate) {
      if (!this.loading && rate !== this.rating) {
        this.rating = rate
        this.onPageChange(1)
      }
    },
    loadPhotos () {
      this.images = []
      this.$api.comment.photo(this.offerId)
        .then((res) => {
          this.images = res
        })
    },
    voteReaction (type, id, voteReactionState) {
      this.loadingVote = true

      this.$api.comment.voteReaction(voteReactionState.user ? 'DEL' : type, id)
        .then(() => {
          if (voteReactionState.user) {
            voteReactionState[type]--
            voteReactionState.user = null
          } else {
            voteReactionState[type]++
            voteReactionState.user = type
          }
        })
        .finally(() => {
          this.loadingVote = false
        })
    },
    scrollToCommentsTop () {
      const element = document.getElementById('comments')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
}
</script>
